import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import type { CountryCode } from "libphonenumber-js";
import { z } from "zod";
import { toast } from "sonner";
import { Check, ChevronDown, Search } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { InlineSvg } from "@/components/ui/inline-svg";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  OnboardingLayout,
  OnboardingCard,
  OnboardingInput,
  OnboardingButton,
  OnboardingSelectTrigger,
} from "@/components/onboarding-layout";
import { Select, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  countryCodeOptions,
  locationOptions,
  getDefaultCountryByLanguage,
} from "@/components/onboarding/country-codes";
import {
  isOptionalCompanyWebsiteUrl,
  normalizeOptionalCompanyWebsiteUrl,
} from "@/components/onboarding/website-url";
import { saveUserProfileServer } from "@/server/onboarding";
import { extractApiErrorMessage } from "@/lib/api/error";
import { logError, logInfo } from "@/lib/structured-log";
import logoSvg from "@/assets/icons/logo3.svg?raw";

export const Route = createFileRoute("/onboarding/profile")({
  component: ProfilePage,
});

const phoneNumberErrorMessage = "Please enter a valid phone number";

const profileFormSchema = z
  .object({
    name: z.string().min(1, "Please enter your name"),
    countryCode: z.string().min(1, "Please select a country code"),
    phoneNumber: z.string().min(1, phoneNumberErrorMessage),
    jobTitle: z.string().min(1, "Please select your role"),
    companyName: z.string().min(1, "Please enter your company name"),
    companySize: z.string().min(1, "Please select your company size"),
    companyWebsite: z
      .string()
      .refine(isOptionalCompanyWebsiteUrl, "Please enter a valid URL")
      .optional(),
    location: z.string().optional(),
    referralSource: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const parsed = parsePhoneNumberFromString(
      data.phoneNumber,
      data.countryCode.toUpperCase() as CountryCode,
    );
    if (!parsed?.isValid()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["phoneNumber"],
        message: phoneNumberErrorMessage,
      });
    }
  });

type ProfileFormData = z.infer<typeof profileFormSchema>;

interface CountryCodeSearchSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
}

function CountryCodeSearchSelect({ value, onValueChange, disabled }: CountryCodeSearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const actionTimerRef = useRef<number | null>(null);
  const selectedOption = countryCodeOptions.find((option) => option.value === value);

  const clearPendingAction = useCallback(() => {
    if (actionTimerRef.current == null) {
      return;
    }
    window.clearTimeout(actionTimerRef.current);
    actionTimerRef.current = null;
  }, []);

  const closeDropdown = useCallback(() => {
    searchInputRef.current?.blur();
    clearPendingAction();
    actionTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      actionTimerRef.current = null;
    }, 0);
  }, [clearPendingAction]);

  const selectOption = useCallback(
    (nextValue: string) => {
      searchInputRef.current?.blur();
      clearPendingAction();
      actionTimerRef.current = window.setTimeout(() => {
        onValueChange(nextValue);
        setQuery("");
        setOpen(false);
        actionTimerRef.current = null;
      }, 0);
    },
    [clearPendingAction, onValueChange],
  );

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return countryCodeOptions;
    }

    return countryCodeOptions.filter((option) => {
      const haystack = `${option.label} ${option.dialCode} ${option.value}`.toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [query]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [closeDropdown, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const focusTimer = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(focusTimer);
  }, [open]);

  useEffect(() => {
    return () => clearPendingAction();
  }, [clearPendingAction]);

  return (
    <div ref={containerRef} className="relative w-[180px] shrink-0">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          "flex h-[44px] w-[180px] items-center justify-between rounded-l-[12px] border border-input bg-background px-2 text-left text-base transition-colors",
          "disabled:cursor-not-allowed disabled:opacity-50",
          open && "ring-ring/50 ring-[3px]",
        )}
        onClick={() => {
          if (disabled) {
            return;
          }
          setOpen((current) => !current);
        }}
      >
        <span className="truncate">{selectedOption?.label ?? value}</span>
        <ChevronDown
          className={cn(
            "ml-2 h-4 w-4 shrink-0 text-[#999999] transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-[52px] z-50 w-[320px] rounded-[12px] border border-[#dfdfdf] bg-white shadow-lg">
          <div className="flex h-11 items-center gap-2 border-b border-[#eeeeee] px-3">
            <Search className="h-4 w-4 shrink-0 text-[#999999]" />
            <input
              ref={searchInputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search country or code"
              className="h-full w-full bg-transparent text-sm outline-none placeholder:text-[#999999]"
            />
          </div>
          <div className="max-h-[260px] overflow-y-auto p-1" role="listbox">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  className={cn(
                    "flex h-10 w-full items-center justify-between rounded-[8px] px-3 text-left text-sm hover:bg-[#f4f4f4]",
                    option.value === value && "bg-[#f4f4f4]",
                  )}
                  onClick={() => selectOption(option.value)}
                >
                  <span className="truncate">{option.label}</span>
                  {option.value === value && <Check className="ml-2 h-4 w-4 shrink-0" />}
                </button>
              ))
            ) : (
              <div className="px-3 py-6 text-center text-sm text-[#666666]">No results</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ProfilePage() {
  const { t, i18n } = useTranslation(["pages", "common"]);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const sourcesObj = t("pages:onboarding.profile.options.sources", {
    returnObjects: true,
  }) as Record<string, string>;
  const rolesObj = t("pages:onboarding.profile.options.roles", {
    returnObjects: true,
  }) as Record<string, string>;
  const companySizesObj = t("pages:onboarding.profile.options.companySizes", {
    returnObjects: true,
  }) as Record<string, string>;
  const availableSources = Object.keys(sourcesObj);
  const availableRoles = Object.keys(rolesObj);
  const availableCompanySizes = Object.keys(companySizesObj);

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      name: "",
      countryCode: getDefaultCountryByLanguage(i18n.language),
      phoneNumber: "",
      jobTitle: undefined,
      companyName: "",
      companySize: undefined,
      companyWebsite: "",
      location: getDefaultCountryByLanguage(i18n.language),
      referralSource: undefined,
    },
  });

  const prevLangRef = useRef(i18n.language);
  useEffect(() => {
    if (prevLangRef.current !== i18n.language) {
      prevLangRef.current = i18n.language;
      const defaultCountry = getDefaultCountryByLanguage(i18n.language);
      form.setValue("countryCode", defaultCountry);
      form.setValue("location", defaultCountry);
    }
  }, [i18n.language, form]);

  const handleSubmit = useCallback(
    async (data: ProfileFormData) => {
      setIsLoading(true);

      const parsedNumber = parsePhoneNumberFromString(
        data.phoneNumber,
        data.countryCode.toUpperCase() as CountryCode,
      );

      if (!parsedNumber?.isValid()) {
        toast.error(t("pages:onboarding.profile.form.phoneInvalid"));
        setIsLoading(false);
        return;
      }

      const companyWebsite = normalizeOptionalCompanyWebsiteUrl(data.companyWebsite);

      logInfo("onboarding.profile.submit.requested", {
        has_company_name: !!data.companyName,
        job_title: data.jobTitle,
        company_size: data.companySize,
        has_website: !!companyWebsite,
        referral_source: data.referralSource,
      });

      try {
        const status = await saveUserProfileServer({
          data: {
            name: data.name || undefined,
            phone_number: parsedNumber.number,
            company_name: data.companyName,
            job_title: data.jobTitle,
            company_size: data.companySize,
            company_website: companyWebsite,
            location: data.location || undefined,
            referral_source: data.referralSource || undefined,
          },
        });

        logInfo("onboarding.profile.submit.succeeded", {
          workspace_slug: status.workspace_slug,
        });
        if (status.workspace_slug) {
          await router.navigate({
            to: "/workspace/$workspaceSlug/new-task",
            params: { workspaceSlug: status.workspace_slug },
          });
        } else {
          await router.invalidate();
        }
      } catch (err) {
        logError("onboarding.profile.submit.failed", {
          error: err instanceof Error ? err.message : "unknown-error",
        });
        toast.error(extractApiErrorMessage(err, t("pages:onboarding.profile.form.submitError")));
      } finally {
        setIsLoading(false);
      }
    },
    [router, t],
  );

  return (
    <OnboardingLayout showLogo={false}>
      <div className="mx-auto flex w-full max-w-[1180px] flex-col items-center gap-8 lg:flex-row lg:items-center lg:justify-between lg:gap-12">
        <section className="flex w-full max-w-[640px] flex-col items-center text-center lg:max-w-[400px] lg:items-start lg:text-left">
          <InlineSvg content={logoSvg} size={132} className="text-black leading-none" />
          <div className="mt-8 space-y-3">
            <h1 className="text-3xl font-medium leading-tight text-black sm:text-4xl">
              {t("pages:onboarding.profile.title")}
            </h1>
            <p className="text-sm leading-6 text-[#666666] sm:text-base">
              {t("pages:onboarding.profile.subtitle")}
            </p>
          </div>
        </section>

        <OnboardingCard
          width="640px"
          className="p-4 sm:p-6 md:p-8 lg:p-[40px] rounded-[12px] sm:rounded-[18px] md:rounded-[24px] lg:mx-0"
        >
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit, (errors) => {
                const firstError = Object.values(errors)[0]?.message;
                if (firstError) toast.error(firstError);
              })}
              autoComplete="off"
              className="flex flex-col space-y-6"
            >
              {/* Name */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-normal text-gray-900">
                      {t("pages:onboarding.profile.form.name")}{" "}
                      <span className="text-red-500 ml-1">*</span>
                    </FormLabel>
                    <FormControl>
                      <OnboardingInput
                        placeholder={t("pages:onboarding.profile.form.namePlaceholder")}
                        {...field}
                        autoComplete="off"
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Phone number */}
              <div className="space-y-2">
                <FormLabel className="text-base font-normal text-gray-900">
                  {t("pages:onboarding.profile.form.phoneNumber")}{" "}
                  <span className="text-red-500 ml-1">*</span>
                </FormLabel>
                <div className="flex">
                  <FormField
                    control={form.control}
                    name="countryCode"
                    render={({ field }) => (
                      <FormItem>
                        <CountryCodeSearchSelect
                          value={field.value}
                          onValueChange={(value) => {
                            field.onChange(value);
                            form.setValue("location", value, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          }}
                          disabled={isLoading}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phoneNumber"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <OnboardingInput
                            placeholder={t("pages:onboarding.profile.form.phonePlaceholder")}
                            {...field}
                            disabled={isLoading}
                            className="rounded-l-none -ml-px"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Role + Company size row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="jobTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-normal text-gray-900">
                        {t("pages:onboarding.profile.form.role")}{" "}
                        <span className="text-red-500 ml-1">*</span>
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        disabled={isLoading}
                      >
                        <FormControl>
                          <OnboardingSelectTrigger>
                            <SelectValue
                              placeholder={t("pages:onboarding.profile.form.selectPlaceholder")}
                            />
                          </OnboardingSelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {availableRoles.map((value) => (
                            <SelectItem key={value} value={value}>
                              {t(`pages:onboarding.profile.options.roles.${value}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="companySize"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-normal text-gray-900">
                        {t("pages:onboarding.profile.form.companySize")}{" "}
                        <span className="text-red-500 ml-1">*</span>
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        disabled={isLoading}
                      >
                        <FormControl>
                          <OnboardingSelectTrigger>
                            <SelectValue
                              placeholder={t("pages:onboarding.profile.form.selectPlaceholder")}
                            />
                          </OnboardingSelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {availableCompanySizes.map((value) => (
                            <SelectItem key={value} value={value}>
                              {t(`pages:onboarding.profile.options.companySizes.${value}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Company name */}
              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-normal text-gray-900">
                      {t("pages:onboarding.profile.form.companyName")}{" "}
                      <span className="text-red-500 ml-1">*</span>
                    </FormLabel>
                    <FormControl>
                      <OnboardingInput
                        placeholder={t("pages:onboarding.profile.form.companyNamePlaceholder")}
                        {...field}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Company website */}
              <FormField
                control={form.control}
                name="companyWebsite"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-normal text-gray-900">
                      {t("pages:onboarding.profile.form.companyWebsite")}
                    </FormLabel>
                    <FormControl>
                      <OnboardingInput
                        placeholder="https://example.com"
                        {...field}
                        autoComplete="off"
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Location + Source row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-normal text-gray-900">
                        {t("pages:onboarding.profile.form.location")}
                      </FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value);
                          form.setValue("countryCode", value, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }}
                        value={field.value}
                        disabled={isLoading}
                      >
                        <FormControl>
                          <OnboardingSelectTrigger>
                            <SelectValue
                              placeholder={t("pages:onboarding.profile.form.locationPlaceholder")}
                            />
                          </OnboardingSelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {locationOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="referralSource"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-normal text-gray-900">
                        {t("pages:onboarding.profile.form.source")}
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        disabled={isLoading}
                      >
                        <FormControl>
                          <OnboardingSelectTrigger>
                            <SelectValue
                              placeholder={t("pages:onboarding.profile.form.selectPlaceholder")}
                            />
                          </OnboardingSelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {availableSources.map((value) => (
                            <SelectItem key={value} value={value}>
                              {t(`pages:onboarding.profile.options.sources.${value}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>

              {/* Submit */}
              <OnboardingButton type="submit" disabled={isLoading}>
                {isLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <Spinner className="w-5 h-5" />
                    {t("pages:onboarding.profile.form.submitting")}
                  </span>
                ) : (
                  t("pages:onboarding.profile.form.submit")
                )}
              </OnboardingButton>
            </form>
          </Form>
        </OnboardingCard>
      </div>
    </OnboardingLayout>
  );
}
